import React, { useRef, useState, useEffect } from 'react';
import { Typography } from '@mui/material';
import AppTooltip from './AppTooltip';

const OverflowTooltip = ({ 
  children, 
  title, 
  tooltipWidth = 300, 
  placement = 'top',
  arrow = true,
  alwaysShow = false,
  component = Typography,
  sx = {},
  maxLines = 1,
  ...props 
}) => {
  const textRef = useRef(null);
  const [showTooltip, setShowTooltip] = useState(false);

  useEffect(() => {
    const checkOverflow = () => {
      if (textRef.current) {
        // Check if it's a Chip component by looking for MuiChip-label
        const labelElement = textRef.current.querySelector('.MuiChip-label');
        if (labelElement) {
          const isOverflowing = labelElement.scrollWidth > labelElement.clientWidth + 1;
          setShowTooltip(isOverflowing);
        } else {
          // Regular overflow check for other components
          const isOverflowing = maxLines > 1 
            ? textRef.current.scrollHeight > textRef.current.clientHeight
            : textRef.current.scrollWidth > textRef.current.clientWidth;
          setShowTooltip(isOverflowing);
        }
      }
    };

    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [title, children, maxLines]);

  const Component = component;

  return (
    <AppTooltip 
      title={alwaysShow || showTooltip ? title : ''} 
      arrow={arrow}
      placement={placement}
      slotProps={{ 
        tooltip: { 
          className: 'app-tooltip',
          sx: { 
            maxWidth: `${tooltipWidth}px`,
            ...(placement?.includes('top') && { marginBottom: '4px !important' })
          } 
        },
        popper: {
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, -4],
              },
            },
          ],
        }
      }}
    >
      <Component 
        ref={textRef}
        sx={{
          overflow: 'hidden',
          minWidth: 0,
          maxWidth: '100%',
          ...(maxLines > 1 ? {
            display: '-webkit-box',
            WebkitLineClamp: maxLines,
            WebkitBoxOrient: 'vertical',
            wordBreak: 'break-word',
            overflowWrap: 'break-word'
          } : {
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            display: 'block'
          }),
          ...sx
        }}
        {...props}
      >
        {children || title}
      </Component>
    </AppTooltip>
  );
};

export default OverflowTooltip;
